import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import PublicFooter from '../PublicFooter/PublicFooter';
import PageMeta from '../SEO/PageMeta';
import NewsArticleReader from './NewsArticleReader';
import logoImg from '../../assets/logo.png';
import templeImg from '../../assets/temple-far1.jpg';
import './NewsSharePage.css';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || '/api' });

// ── Guest view tracking ───────────────────────────────────────────────────────
const VIEWS_KEY = 'civilfox_guest_views';
const MAX_FREE  = 5;

function getGuestViews() {
  try { return JSON.parse(localStorage.getItem(VIEWS_KEY) || '[]'); } catch { return []; }
}
function recordView(id) {
  if (!id) return;
  const views = getGuestViews();
  if (!views.includes(id)) {
    views.push(id);
    localStorage.setItem(VIEWS_KEY, JSON.stringify(views.slice(-50)));
  }
}
function guestViewCount() { return getGuestViews().length; }

/* ── Small full-screen states share the temple backdrop ── */
function Backdrop({ children }) {
  return (
    <div className="nw nw--center" style={{ backgroundImage: `url(${templeImg})` }}>
      {children}
      <PublicFooter />
    </div>
  );
}

function SignupWall() {
  return (
    <Backdrop>
      <div className="nw-card">
        <span className="nw-logo nw-logo--lg"><img src={logoImg} alt="" /></span>
        <h2>Keep reading for free</h2>
        <p>Sign up free to read unlimited articles.</p>
        <Link to="/register" className="nw-btn">Sign up</Link>
        <Link to="/login" className="nw-link">Log in</Link>
      </div>
    </Backdrop>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN PAGE
// ─────────────────────────────────────────────────────────────────────────────
export default function NewsSharePage() {
  const { id } = useParams();

  const [articles,    setArticles]    = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading,     setLoading]     = useState(true);
  const [notFound,    setNotFound]    = useState(false);
  const [showWall,    setShowWall]    = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page,        setPage]        = useState(1);
  const [hasMore,     setHasMore]     = useState(false);
  const [total,       setTotal]       = useState(0);

  const scrollRef    = useRef(null);
  const touchStartX  = useRef(null);
  const touchStartY  = useRef(null);
  const swipeLocked  = useRef(null);

  const isLoggedIn = !!localStorage.getItem('vidyacards_token');

  // ── Load: shared article first, then live feed ───────────────────────────
  useEffect(() => {
    async function init() {
      try {
        if (!isLoggedIn && !getGuestViews().includes(id) && guestViewCount() >= MAX_FREE) {
          setShowWall(true);
          setLoading(false);
          return;
        }
        // Use the server-rendered article when available, otherwise fetch it.
        const preloaded = window.__NEWS_DATA__?._id === id ? window.__NEWS_DATA__ : null;
        if (preloaded) window.__NEWS_DATA__ = null;
        const shareRes = preloaded ? null : await api.get(`/news/article/${id}`);
        const shared = preloaded || shareRes?.data.article;

        if (!shared) { setNotFound(true); setLoading(false); return; }

        // Mark it so it survives cleanup
        api.post(`/news/share/${id}`).catch(() => {});

        const feedRes = await api.get('/news/live', { params: { limit: 30, page: 1 } });
        const feed    = (feedRes.data.articles || []).filter(a => a._id !== shared._id);

        setArticles([shared, ...feed]);
        setTotal((feedRes.data.total || 0) + 1);
        setHasMore(1 < (feedRes.data.pages || 1));
        setPage(1);

        if (!isLoggedIn) recordView(id);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [id, isLoggedIn]);

  // ── Load more pages ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!hasMore || loadingMore) return;
    if (activeIndex >= articles.length - 5) {
      const next = page + 1;
      setLoadingMore(true);
      api.get('/news/live', { params: { limit: 30, page: next } })
        .then(res => {
          const more = (res.data.articles || []).filter(a => !articles.find(x => x._id === a._id));
          setArticles(prev => [...prev, ...more]);
          setHasMore(next < (res.data.pages || 1));
          setPage(next);
        })
        .catch(() => {})
        .finally(() => setLoadingMore(false));
    }
  }, [activeIndex, articles.length, hasMore, loadingMore, page, articles]);

  // Each new article starts at the top
  useEffect(() => { scrollRef.current?.scrollTo?.(0, 0); }, [activeIndex]);

  // ── Navigation ─────────────────────────────────────────────────────────────
  const goNext = useCallback(() => {
    if (activeIndex >= articles.length - 1) return;
    if (!isLoggedIn && guestViewCount() >= MAX_FREE) { setShowWall(true); return; }
    const next = activeIndex + 1;
    if (!isLoggedIn) recordView(articles[next]?._id);
    setActiveIndex(next);
  }, [activeIndex, articles, isLoggedIn]);

  const goPrev = useCallback(() => setActiveIndex(i => Math.max(0, i - 1)), []);

  // ── Keyboard: left / right only, so up / down still scroll the article ─────
  useEffect(() => {
    const h = e => {
      const t = e.target;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft')  goPrev();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [goNext, goPrev]);

  // ── Swipe ──────────────────────────────────────────────────────────────────
  const onTouchStart = e => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
    swipeLocked.current = null;
  };
  const onTouchMove = e => {
    if (touchStartX.current === null || swipeLocked.current !== null) return;
    const dx = e.touches[0].clientX - touchStartX.current;
    const dy = e.touches[0].clientY - touchStartY.current;
    if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
      swipeLocked.current = Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical';
    }
  };
  const onTouchEnd = e => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (swipeLocked.current === 'horizontal' && Math.abs(dx) > 60) {
      if (dx < 0) goNext(); else goPrev();
    }
    touchStartX.current = null;
    touchStartY.current = null;
    swipeLocked.current = null;
  };

  // ── Share ──────────────────────────────────────────────────────────────────
  const handleShare = target => api.post(`/news/share/${target._id}`).catch(() => {});

  // ── Render states ──────────────────────────────────────────────────────────
  if (loading) return (
    <Backdrop>
      <div className="nw-card nw-card--plain"><span className="nw-spin" role="status" aria-label="Loading" /></div>
    </Backdrop>
  );

  if (notFound) return (
    <Backdrop>
      <div className="nw-card">
        <span className="nw-logo nw-logo--lg"><img src={logoImg} alt="" /></span>
        <h2>Article not found</h2>
        <p>News refreshes every hour.</p>
        <Link to="/" className="nw-btn">Go to CivilFox</Link>
      </div>
    </Backdrop>
  );

  if (showWall) return <SignupWall />;

  const article  = articles[activeIndex];
  const count    = total || articles.length;
  const freeLeft = Math.max(0, MAX_FREE - guestViewCount());
  const summary  = (article?.upscExplanationStructured?.newsStory || article?.description || '').slice(0, 155);

  return (
    <div className="nw" style={{ backgroundImage: `url(${templeImg})` }}>

      {article && (
        <PageMeta
          title={`${article.title} | UPSC Current Affairs | CivilFox`}
          description={summary}
          path={`/news/${encodeURIComponent(article._id)}`}
          type="article"
          image={article.imageUrl || undefined}
          structuredData={{
            '@context': 'https://schema.org',
            '@type': 'NewsArticle',
            headline: article.title,
            description: summary,
            datePublished: article.publishedAt,
            dateModified: article.updatedAt || article.publishedAt,
            image: article.imageUrl ? [article.imageUrl] : undefined,
            mainEntityOfPage: `https://civilfox.app/news/${article._id}`,
            author: { '@type': 'Organization', name: article.source || 'CivilFox' },
            publisher: { '@type': 'Organization', name: 'CivilFox', url: 'https://civilfox.app' },
            articleSection: article.subject,
          }}
        />
      )}

      {/* Top bar over the temple: logo, pager, sign up */}
      <header className="nw-bar">
        <Link to="/" className="nw-logo" aria-label="CivilFox home"><img src={logoImg} alt="" /></Link>

        <div className="nw-pager" role="group" aria-label="Articles">
          <button type="button" className="nw-nbtn" onClick={goPrev} disabled={activeIndex === 0} aria-label="Previous article">
            <ChevronLeft size={18} />
          </button>
          <span className="nw-pos"><b>{activeIndex + 1}</b> / {count}</span>
          <button type="button" className="nw-nbtn" onClick={goNext}
            disabled={activeIndex === articles.length - 1 && !hasMore} aria-label="Next article">
            <ChevronRight size={18} />
          </button>
          {!isLoggedIn && <small className="nw-free">{freeLeft} free left</small>}
        </div>

        {!isLoggedIn ? <Link to="/register" className="nw-signup">Sign up</Link> : <span />}
      </header>

      {/* The article sheet: temple stays visible above and on both sides */}
      <main className="nw-sheet" onTouchStart={onTouchStart} onTouchMove={onTouchMove} onTouchEnd={onTouchEnd}>
        <div className="nw-progress" aria-hidden="true">
          <span style={{ width: `${((activeIndex + 1) / Math.max(count, articles.length, 1)) * 100}%` }} />
        </div>

        <div className="nw-scroll" ref={scrollRef}>
          {article && (
            <article className="nw-article" key={article._id}>
              <NewsArticleReader article={article} onShare={() => handleShare(article)} />
            </article>
          )}

          {!isLoggedIn && (
            <div className="nw-cta">
              <span>Turn today’s news into UPSC flashcards.</span>
              <Link to="/register" className="nw-btn nw-btn--sm">Sign up</Link>
            </div>
          )}

          {loadingMore && <p className="nw-more">Loading more…</p>}
          <PublicFooter />
        </div>
      </main>
    </div>
  );
}